/**
 * Direction B: pay strata, the drill-in when the Pay line is clicked. One stripe per check:
 * above the axis what was earned (regular, overtime, other -- or gross when the split can't
 * be made), below it where the money went (net, deductions, taxes). One money scale serves
 * both halves (mirrored), so the outline is gross pay over time. The base rate runs above on
 * its own scale; WFM daily hours run below, one cell per day, under the check that paid them.
 */
import { useEffect, useState } from 'react'
import { getRegisterCheckDetail } from '../../../api'
import LoadError from '../../../components/LoadError'
import { dayMs, fmtDate, fmtHours, fmtMoney, fmtPay, linear, payMetric, payText, strata } from './lifelineModel'
import { Tip } from './Tip'

// The strata's headers, in plain words (their tooltips).
const HELP = {
  earned: 'Above the line: what each check earned, split into regular, overtime and other pay (PTO, holiday and the rest), or gross when the split can\'t be made.',
  went: 'Below the line: where the gross went, net pay, deductions and taxes. One money scale serves both halves, so each stripe\'s height is its gross.',
  rate: 'The base pay in force on each check\'s pay date, on its own scale: the annual salary for a salaried person, the hourly rate for an hourly one.',
  wfm: 'WFM timecard hours, one cell per day of the check\'s pay period: darker is more hours; a ringed cell is over 8 hours.',
}

const COL = 14
const GAP = 3
const TOP = 70
const AXIS = 230
const HALF = 140
const COLORS = {
  regular: '#0d5f58', overtime: '#3fa898', other: '#a9d8cf', gross: '#0d5f58',
  net: '#2e3a4a', deductions: '#8592a3', taxes: '#c3cad3',
}
const LABEL = { regular: 'Regular', overtime: 'Overtime', other: 'PTO, holiday + other', gross: 'Gross', net: 'Net pay', deductions: 'Deductions', taxes: 'Taxes' }

function activate(fn) {
  return { onClick: fn, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn() } } }
}

export default function PayStrata({ customerId, data, markers, selectedCheck, onSelectCheck, peersOn }) {
  const ps = data.sections.pay_statements
  const wfm = data.sections.wfm_daily
  const paySegs = data.sections.pay?.available ? data.sections.pay.segments : []
  const [detail, setDetail] = useState(null)
  const [detailError, setDetailError] = useState(null)
  const [detailTry, setDetailTry] = useState(0)

  const sel = ps?.available ? ps.checks.find(c => c.pay_identifier === selectedCheck) : null
  useEffect(() => {
    setDetail(null)
    setDetailError(null)
    if (!sel) return undefined
    let live = true
    getRegisterCheckDetail(customerId, sel.pay_identifier)
      .then(d => { if (live) setDetail(d) })
      .catch(e => { if (live) setDetailError(e) })
    return () => { live = false }
  }, [customerId, sel?.pay_identifier, detailTry])

  if (!ps?.available) {
    return <div className="ll-card"><h2>Pay strata</h2><p className="ll-na"><b>Not available.</b> {ps?.reason}</p></div>
  }
  const cols = strata(ps.checks)
  const width = Math.max(640, 120 + cols.length * (COL + GAP) + 40)
  const maxUp = Math.max(1, ...cols.map(c => c.up.reduce((a, b) => a + b.v, 0)), ...cols.map(c => c.down.reduce((a, b) => a + b.v, 0)))
  const h = linear(0, maxUp, 0, HALF)                       // the one money scale, both halves
  const xOf = (i) => 120 + i * (COL + GAP)
  const split = cols.some(c => c.up[0].k === 'regular')

  // base rate on its own scale, above the stripes
  const rateMetric = payMetric(paySegs, data.person)        // a salary for a salaried person
  const segAt = (day) => {
    const t = dayMs(day)
    return paySegs.find(s => dayMs(s.start) <= t && (s.end == null || dayMs(s.end) > t)) || null
  }
  const rateAt = (day) => segAt(day)?.[rateMetric] ?? null
  const rates = cols.map(c => rateAt(c.day)).filter(v => v != null)
  const ry = rates.length ? linear(Math.min(...rates), Math.max(...rates) || 1, 54, 22) : null

  // WFM: the days each check's period covers
  const days = wfm?.available ? new Map(wfm.days.map(d => [d.date, d])) : null
  // WFM coverage: cells are drawn only for checks whose period falls inside it
  const wfmFrom = days ? dayMs(wfm.days[0].date) : null
  const wfmTo = days ? dayMs(wfm.days[wfm.days.length - 1].date) : null
  const wfmY = AXIS + HALF + 20
  // the tick is an hourly peer median times the check's hours: hourly people only
  const peers = data.sections.peers
  const peer = peersOn && peers?.available && (peers.metric || 'hourly_rate') === 'hourly_rate' && rateMetric === 'hourly_rate' ? peers.hourly_rate : null
  const flagged = new Map(markers.filter(m => m.check).map(m => [m.check.pay_identifier, m]))
  const height = wfmY + (days ? 16 * 6 + 24 : 10)

  return (
    <div className="ll-card">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2>Pay strata</h2>
        <span className="ll-mono ll-note">{ps.checks.length} checks · {fmtDate(ps.checks[0].pay_date)} – {fmtDate(ps.checks[ps.checks.length - 1].pay_date)}</span>
      </div>
      <div className="ll-legend">
        <Tip text={HELP.earned}><span className="ll-eyebrow" tabIndex={0}>Earned ↑</span></Tip>
        {(split ? ['regular', 'overtime', 'other'] : ['gross']).map(k => <span key={k}><i style={{ background: COLORS[k] }} />{LABEL[k]}</span>)}
        <Tip text={HELP.went}><span className="ll-eyebrow" tabIndex={0}>Went to ↓</span></Tip>
        {['net', 'deductions', 'taxes'].map(k => <span key={k}><i style={{ background: COLORS[k] }} />{LABEL[k]}</span>)}
      </div>
      {!split && ps.split?.reason && <p className="ll-note">{ps.split.reason}</p>}
      {ps.note && <p className="ll-note">{ps.note}</p>}
      <div className="ll-table-wrap">
        <svg className="ll-strata" viewBox={`0 0 ${width} ${height}`} style={{ minWidth: Math.min(width, 1400) }}
             role="group" aria-label="Pay strata: one stripe per check, earnings above the axis and deductions, taxes and net pay below">
          <Tip text={HELP.rate}>
            <g tabIndex={0} role="img" aria-label="Base rate">
              <text x={0} y={14} className="ll-mono" style={{ letterSpacing: '0.06em', fill: 'var(--ll-muted)' }}>BASE RATE</text>
              <rect className="ll-focus-ring" x={-2} y={2} width={76} height={16} rx={4} />
            </g>
          </Tip>
          {ry && cols.map((c, i) => {
            const r = rateAt(c.day)
            return r == null ? null : <line key={`r${i}`} x1={xOf(i)} x2={xOf(i) + COL + GAP} y1={ry(r)} y2={ry(r)} stroke="var(--ll-ink)" strokeWidth={2.5} />
          })}
          {ry && rates.length > 0 && (
            <text x={0} y={40} className="ll-mono">{rateMetric === 'hourly_rate' ? `${fmtMoney(Math.min(...rates))}–${fmtMoney(Math.max(...rates))}/h` : `${fmtMoney(Math.min(...rates), 0)}–${fmtMoney(Math.max(...rates), 0)}/yr`}</text>
          )}
          <text x={0} y={TOP + 10} className="ll-mono" style={{ fill: 'var(--ll-muted)' }}>{fmtMoney(maxUp, 0)}</text>
          <text x={0} y={AXIS + 4} className="ll-mono" style={{ fill: 'var(--ll-muted)' }}>$0</text>
          <line x1={116} x2={width - 10} y1={AXIS} y2={AXIS} stroke="var(--ll-ink)" strokeWidth={2} />
          {cols.map((c, i) => {
            const x = xOf(i)
            let yUp = AXIS
            let yDown = AXIS + 2
            const isSel = selectedCheck === c.id
            const flag = flagged.get(c.id)
            const label = `Check paid ${fmtDate(c.day)}: gross ${fmtMoney(c.check.gross)}, net ${fmtMoney(c.check.net)}, deductions ${fmtMoney(c.check.deductions)}, taxes ${fmtMoney(c.check.taxes)}${flag ? ', has a finding' : ''}`
            return (
              <g key={c.id} className="ll-hit" tabIndex={0} role="button" aria-label={label} aria-pressed={isSel}
                 {...activate(() => onSelectCheck(c.id))}>
                <title>{label}</title>
                {c.up.map(p => {
                  const hh = h(p.v); yUp -= hh
                  return hh > 0 ? <rect key={p.k} x={x} y={yUp} width={COL} height={hh} fill={COLORS[p.k]} /> : null
                })}
                {c.down.map(p => {
                  const hh = h(p.v); const y0 = yDown; yDown += hh
                  return hh > 0 ? <rect key={p.k} x={x} y={y0} width={COL} height={hh} fill={COLORS[p.k]} /> : null
                })}
                {peer && c.check.hours != null && (
                  <line x1={x - 1} x2={x + COL + 1} y1={AXIS - h(peer.median * c.check.hours)} y2={AXIS - h(peer.median * c.check.hours)} stroke="var(--ll-peer-line)" strokeWidth={2} />
                )}
                {flag && (
                  <rect x={x - 3} y={yUp - 6} width={COL + 6} height={AXIS - yUp + 12} rx={4} fill="none"
                        stroke={flag.severity === 'high' ? 'var(--ll-high)' : 'var(--ll-medium)'} strokeWidth={2} />
                )}
                <rect className="ll-focus-ring" x={x - 4} y={TOP - 4} width={COL + 8} height={HALF * 2 + 10} rx={4} />
                {isSel && <rect x={x - 4} y={TOP - 4} width={COL + 8} height={HALF * 2 + 10} rx={4} fill="none" stroke="var(--ll-job)" strokeWidth={2} />}
                {days && (() => {
                  const a = dayMs(c.check.period_start), b = dayMs(c.check.period_end)
                  if (a == null || b == null || b < wfmFrom || a > wfmTo) return null
                  const cells = []
                  for (let t = a, k = 0; t <= b && k < 16; t += 86400000, k++) {
                    const d = days.get(new Date(t).toISOString().slice(0, 10))
                    const hrs = d ? d.hours : 0
                    cells.push(<rect key={k} x={x + 2} y={wfmY + k * 6} width={COL - 4} height={5}
                                     fill={hrs ? `rgba(11,122,108,${Math.min(1, 0.2 + hrs / 12)})` : '#eef0ec'}
                                     stroke={hrs > 8 ? 'var(--ll-medium)' : 'none'} strokeWidth={0.75} />)
                  }
                  return cells
                })()}
              </g>
            )
          })}
          {days && (
            <Tip text={HELP.wfm}>
              <g tabIndex={0} role="img" aria-label="WFM days">
                <text x={0} y={wfmY + 10} className="ll-mono" style={{ fill: 'var(--ll-muted)' }}>WFM DAYS</text>
                <rect className="ll-focus-ring" x={-2} y={wfmY - 2} width={70} height={16} rx={4} />
              </g>
            </Tip>
          )}
          {days && <text x={0} y={wfmY + 24} style={{ fontSize: 10, fill: 'var(--ll-muted)' }}>ringed: over 8 h</text>}
        </svg>
      </div>
      {!days && <p className="ll-note">Daily hours: {wfm?.reason}</p>}
      {peer && <p className="ll-note">Tick on a stripe: the peer median rate × that check's hours (same job and location, n = {data.sections.peers.n}).</p>}

      {sel ? (
        <div className="ll-card" style={{ background: 'var(--gray-50)' }} aria-live="polite">
          <div className="ll-eyebrow">Check paid {fmtDate(sel.pay_date)} · period {fmtDate(sel.period_start, false)} – {fmtDate(sel.period_end)}</div>
          <div className="ll-detail-grid ll-mono" style={{ fontSize: 12.5 }}>
            <div>Gross <b>{fmtMoney(sel.gross)}</b></div>
            <div>Net <b>{fmtMoney(sel.net)}</b></div>
            <div>Deductions <b>{fmtMoney(sel.deductions)}</b></div>
            <div>Taxes <b>{fmtMoney(sel.taxes)}</b></div>
            <div>Hours <b>{fmtHours(sel.hours)}</b></div>
            {rateMetric === 'annual_salary' && segAt(sel.pay_date) && <div>Salary <b>{payText(segAt(sel.pay_date), 'salaried')}</b></div>}
            <div>{rateMetric === 'annual_salary' ? 'Hourly rate on check' : 'Rate'} <b>{sel.pay_rate != null ? fmtPay(sel.pay_rate, 'hourly_rate') : '—'}</b></div>
          </div>
          {detailError && <LoadError inline what="this check's lines" error={detailError} onRetry={() => setDetailTry(n => n + 1)} />}
          {!detail && !detailError && <span className="ll-note">Loading the check's lines…</span>}
          {detail && (
            <div className="ll-detail-grid">
              {[['Earnings', detail.earnings, r => r.payDescription || r.payCode, r => r.amount, r => r.hours],
                ['Deductions', detail.deductions, r => r.deductionDescription || r.deductionCode, r => r.employeeAmount, () => null],
                ['Taxes', detail.taxes, r => r.taxDescription || r.taxCode, r => r.amount, () => null]].map(([title, rows, name, amt, hrs]) => (
                <div key={title}>
                  <div className="ll-eyebrow">{title}</div>
                  {(rows || []).length === 0 && <div className="ll-note">None on this check.</div>}
                  <table className="ll-table ll-mono"><tbody>
                    {(rows || []).map((r, i) => (
                      <tr key={i}><td>{name(r)}</td><td className="num">{hrs(r) != null ? fmtHours(hrs(r)) : ''}</td><td className="num">{fmtMoney(amt(r))}</td></tr>
                    ))}
                  </tbody></table>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : <p className="ll-note">Select a stripe to see that check's earnings, deductions and taxes.</p>}
    </div>
  )
}
